import { buildExamAnalytics, num } from "../examAnalytics/examAnalyticsHelpers";

const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();

// getExamResultDetail's students[] carries no class_id/branch_id per student — only
// sectionName — so class-level grouping can only ever be the exam's own single
// className (a real, single-class breakdown, not a fabricated multi-class one), and
// there is no branch-level data available in this response at all.
export const mapExamDetailToAnalytics = (examDetail, listMeta, opts = {}) => {
    const { authorizedClassNames = [], authorizedSectionNames = [], authorizedSubjectNames = [] } = opts;

    const classAllowed = !authorizedClassNames.length || authorizedClassNames.map(norm).includes(norm(examDetail.className));
    const rawStudents = classAllowed ? examDetail.students || [] : [];
    const sectionScoped = authorizedSectionNames.length
        ? rawStudents.filter((s) => authorizedSectionNames.map(norm).includes(norm(s.sectionName)))
        : rawStudents;

    const students = sectionScoped.map((s) => ({
        id: s.studentUserId,
        student_name: s.name,
        profile_pic: null,
        class_id: examDetail.className,
        class_name: examDetail.className,
        section_id: s.sectionName,
        section_name: s.sectionName,
        marks: s.marks,
        accuracy: s.pct,
        exam_rank: s.rank,
        overall_rank: null,
        attended: true,
        subjectMarks: (s.subjects || []).map((sub) => ({
            subjectId: norm(sub.subject),
            subjectName: sub.subject,
            subjectMarks: sub.marks,
            subjectRank: null,
            subjectTotalQuestions: sub.total,
            subjectRight: sub.correct,
            subjectWrong: sub.wrong,
        })),
    }));

    // Absent students carry no per-student row in this API at all — the gap between
    // the exam list's totalStudents and this response's appeared/students.length is
    // represented as bare placeholders (no marks/accuracy invented) purely so
    // overall.totalStudents/absent stay accurate.
    const knownTotal = (listMeta && num(listMeta.totalStudents)) || num(examDetail.appeared) || students.length;
    const absentCount = Math.max(0, knownTotal - students.length);
    const absentPlaceholders = Array.from({ length: classAllowed ? absentCount : 0 }, (_, idx) => ({
        id: `absent-${idx}`,
        student_name: "-",
        class_id: examDetail.className,
        class_name: examDetail.className,
        section_id: "-",
        section_name: "-",
        marks: 0,
        accuracy: 0,
        exam_rank: null,
        overall_rank: null,
        attended: false,
        subjectMarks: [],
    }));

    return buildExamAnalytics([...students, ...absentPlaceholders], {
        authorizedSubjectIds: authorizedSubjectNames.map(norm),
        authorizedClassIds: [],
        authorizedSectionIds: [],
    });
};

// Shared by Subject/Chapter/Topic Analysis — same aggregate shape grouped on a
// different question field.
const aggregateByField = (questions, keyFn) => {
    const map = {};
    questions.forEach((q) => {
        const key = keyFn(q) || "-";
        if (!map[key]) {
            map[key] = { key, questions: 0, correct: 0, wrong: 0, unattempted: 0, correctPct: [], wrongPct: [], unattemptedPct: [], errorPct: [] };
        }
        const bucket = map[key];
        bucket.questions += 1;
        bucket.correct += num(q.correct);
        bucket.wrong += num(q.wrong);
        bucket.unattempted += num(q.unattempted);
        bucket.correctPct.push(num(q.correctPct));
        bucket.wrongPct.push(num(q.wrongPct));
        bucket.unattemptedPct.push(num(q.unattemptedPct));
        bucket.errorPct.push(num(q.errorPct));
    });
    const avg = (list) => (list.length ? Math.round((list.reduce((a, b) => a + b, 0) / list.length) * 100) / 100 : 0);
    return Object.values(map)
        .map((b) => ({
            id: b.key,
            name: b.key,
            questions: b.questions,
            correct: b.correct,
            wrong: b.wrong,
            unattempted: b.unattempted,
            accuracy: avg(b.correctPct),
            correctPct: avg(b.correctPct),
            wrongPct: avg(b.wrongPct),
            unattemptedPct: avg(b.unattemptedPct),
            errorPct: avg(b.errorPct),
        }))
        .sort((a, b) => b.accuracy - a.accuracy);
};

// The API returns pre-classified okQuestions/warnQuestions/criticalQuestions totals
// but no per-question quality field — this threshold mirrors those buckets closely
// enough for a per-row badge, documented here as a derived label, not a real field.
export const classifyQuestionQuality = (correctPct) => {
    const pct = num(correctPct);
    if (pct >= 70) return "OK";
    if (pct >= 40) return "Warning";
    return "Critical";
};

export const buildQuestionAnalytics = (data, authorizedSubjectNames = []) => {
    const allQuestions = (data && data.questions) || [];
    const questions = (authorizedSubjectNames.length
        ? allQuestions.filter((q) => authorizedSubjectNames.map(norm).includes(norm(q.subject)))
        : allQuestions
    ).map((q) => ({ ...q, id: q.questionId ?? q.slno, quality: classifyQuestionQuality(q.correctPct) }));

    const overall = {
        takers: num(data && data.takers),
        totalQuestions: questions.length || num(data && data.totalQuestions),
        totalCorrect: num(data && data.totalCorrect),
        totalWrong: num(data && data.totalWrong),
        totalUnattempted: num(data && data.totalUnattempted),
        correctPct: num(data && data.correctPct),
        wrongPct: num(data && data.wrongPct),
        unattemptedPct: num(data && data.unattemptedPct),
        errorPct: num(data && data.errorPct),
        okQuestions: num(data && data.okQuestions),
        warnQuestions: num(data && data.warnQuestions),
        criticalQuestions: num(data && data.criticalQuestions),
    };

    const subjectAnalysis = aggregateByField(questions, (q) => q.subject);
    const chapterAnalysis = aggregateByField(questions, (q) => q.chapter);
    const topicAnalysis = aggregateByField(questions, (q) => q.topic);
    const qualityDistribution = [
        { name: "OK", count: overall.okQuestions },
        { name: "Warning", count: overall.warnQuestions },
        { name: "Critical", count: overall.criticalQuestions },
    ].filter((d) => d.count > 0);

    return { overall, questions, subjectAnalysis, chapterAnalysis, topicAnalysis, qualityDistribution };
};
